import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MmdPluginMaterial } from "./mmdPluginMaterial";
/**
 * MMD standard material
 *
 * This material is an extension of the `StandardMaterial` and includes features for mmd's shading specifications
 *
 * The user can decide whether to activate each mmd shading specification
 *
 * If you disable all features, it behaves the same as `StandardMaterial`
 */
export class MmdStandardMaterial extends StandardMaterial {
    _pluginMaterial;
    /**
     * Whether to use the outline rendering (default: false)
     */
    renderOutline = false;
    /**
     * Outline width (default: 0.01)
     */
    outlineWidth = 0.01;
    /**
     * Outline color (default: (0, 0, 0))
     */
    outlineColor = new Color3(0, 0, 0);
    /**
     * Outline alpha (default: 1.0)
     */
    outlineAlpha = 1.0;
    /**
     * Create a new MMD standard material
     * @param name Define the name of the material in the scene
     * @param scene The scene the material belongs to
     */
    constructor(name, scene) {
        super(name, scene);
        this.specularColor = new Color3(0, 0, 0);
        const pluginMaterial = this._pluginMaterial = new MmdPluginMaterial(this);
        pluginMaterial.isEnabled = true;
        pluginMaterial.ignoreDiffuseWhenToonTextureIsNull = true;
    }
    /**
     * Get or set sphere texture
     */
    get sphereTexture() {
        return this._pluginMaterial.sphereTexture;
    }
    set sphereTexture(value) {
        this._pluginMaterial.sphereTexture = value;
    }
    /**
     * Get or set sphere texture blend mode (default: MmdPluginMaterialSphereTextureBlendMode.Add)
     */
    get sphereTextureBlendMode() {
        return this._pluginMaterial.sphereTextureBlendMode;
    }
    set sphereTextureBlendMode(value) {
        this._pluginMaterial.sphereTextureBlendMode = value;
    }
    /**
     * Get or set toon texture
     */
    get toonTexture() {
        return this._pluginMaterial.toonTexture;
    }
    set toonTexture(value) {
        this._pluginMaterial.toonTexture = value;
    }
    /**
     * If toe on texture is not set, decide whether to treat it as if it had white toon texture applied (default: true)
     *
     * In general, in order to get a stylized rendering, it's better to do true
     *
     * and if you want a more realistic rendering, it's better to do false
     */
    get ignoreDiffuseWhenToonTextureIsNull() {
        return this._pluginMaterial.ignoreDiffuseWhenToonTextureIsNull;
    }
    set ignoreDiffuseWhenToonTextureIsNull(value) {
        this._pluginMaterial.ignoreDiffuseWhenToonTextureIsNull = value;
    }
    /**
     * Get or set whether to use the texture color (default: (1, 1, 1, 1))
     *
     * If this property is first accessed as set, the shader is recompiled to support texture color properties
     *
     * After that, the feature is no longer turned off to prevent shader recompilation
     *
     * These features are subject to change at a later date
     */
    get textureColor() {
        this._pluginMaterial.useTextureColor = true;
        return this._pluginMaterial.textureColor;
    }
    set textureColor(value) {
        this._pluginMaterial.useTextureColor = true;
        this._pluginMaterial.textureColor = value;
    }
    /**
     * Get or set whether to use the sphere texture color (default: (1, 1, 1, 1))
     *
     * If this property is first accessed as set, the shader is recompiled to support texture color properties
     *
     * After that, the feature is no longer turned off to prevent shader recompilation
     *
     * These features are subject to change at a later date
     */
    get sphereTextureColor() {
        this._pluginMaterial.useSphereTextureColor = true;
        return this._pluginMaterial.sphereTextureColor;
    }
    set sphereTextureColor(value) {
        this._pluginMaterial.useSphereTextureColor = true;
        this._pluginMaterial.sphereTextureColor = value;
    }
    /**
     * Get or set whether to use the toon texture color (default: (1, 1, 1, 1))
     *
     * If this property is first accessed as set, the shader is recompiled to support texture color properties
     *
     * After that, the feature is no longer turned off to prevent shader recompilation
     *
     * These features are subject to change at a later date
     */
    get toonTextureColor() {
        this._pluginMaterial.useToonTextureColor = true;
        return this._pluginMaterial.toonTextureColor;
    }
    set toonTextureColor(value) {
        this._pluginMaterial.useToonTextureColor = true;
        this._pluginMaterial.toonTextureColor = value;
    }
}
